// src/encounter/inserted-templates/inserted-template-block.tsx
// One template added from the "/" menu: its title, the template drawn by the DS `QuestionnaireRenderer` (the same
// renderer, with the props the frame v2 field runs use), a quiet save status and "Remove". The answers are this set's
// own (`use-inserted-templates.ts`); nothing here touches the note's primary response.
import { useCallback, useMemo, useState } from 'react';
import { Banner, Button, ModalYesNo, NoteBlock, QuestionnaireRenderer, type QuestionnaireResponse } from '@scripsteam/scrips-signal-ds';
import { fhirToDsQuestionnaire } from '../lib/fhir-to-ds-questionnaire';
import { calculatedIds } from '../structured-note/note-adapter';
import { V2_META } from '../lib/design-v2';
import type { InsertedSetStatus, InsertedSetView } from './use-inserted-templates';

const STATUS_TEXT: Record<InsertedSetStatus, string> = {
  idle: '',
  saving: 'Saving…',
  saved: 'Saved',
  conflict: 'This was changed somewhere else. Your answers are still here.',
  error: 'Not saved.',
};

export interface InsertedTemplateBlockProps {
  set: InsertedSetView;
  role: 'nurse' | 'physician';
  onChange: (answerSetId: string, response: QuestionnaireResponse) => void;
  onRetry: (answerSetId: string) => void;
  onRemove: (answerSetId: string) => void;
}

export function InsertedTemplateBlock({ set, role, onChange, onRetry, onRemove }: InsertedTemplateBlockProps) {
  const [confirming, setConfirming] = useState(false);
  const questionnaire = useMemo(() => (set.questionnaire ? fhirToDsQuestionnaire(set.questionnaire) : null), [set.questionnaire]);
  const calcIds = useMemo(() => (questionnaire ? calculatedIds(questionnaire.item) : new Set<string>()), [questionnaire]);
  const emit = useCallback(
    (emitted: QuestionnaireResponse) => {
      // A computed value the renderer adds is never saved as an answer.
      onChange(set.answerSetId, calcIds.size ? { ...emitted, answers: emitted.answers.filter((a) => !calcIds.has(a.linkId)) } : emitted);
    },
    [onChange, set.answerSetId, calcIds],
  );

  const retryLabel = set.status === 'conflict' ? 'Save again' : 'Try again';
  return (
    <>
      <NoteBlock
        id={`inserted-template-${set.answerSetId}`}
        title={set.title}
        icon="section-docs"
        placement="wayfinding"
        titleTone="text-primary"
        border="card"
        labelTone="secondary"
        actions={
          <Button variant="tertiary" size="sm" onClick={() => setConfirming(true)} aria-label={`Remove ${set.title}`}>
            Remove
          </Button>
        }
      >
        <div data-inserted-template={set.answerSetId} className="flex flex-col gap-2">
          {set.templateState === 'loading' && (
            <p className={`${V2_META} m-0`} role="status">
              Loading…
            </p>
          )}
          {set.templateState === 'error' && (
            <Banner variant="error" role="alert" title="Template not loaded">
              This template could not be loaded.
            </Banner>
          )}
          {questionnaire && set.response && (
            <QuestionnaireRenderer
              questionnaire={questionnaire}
              aria-label={set.title}
              response={set.response}
              onChange={emit}
              unwrapPageGroup
              mode="capture"
              buttonCursor="pointer"
              role={role}
            />
          )}
          {STATUS_TEXT[set.status] &&
            (set.status === 'conflict' || set.status === 'error' ? (
              <Banner
                variant={set.status === 'conflict' ? 'warning' : 'error'}
                role="alert"
                title={set.status === 'conflict' ? 'Changed somewhere else' : 'Not saved'}
                data-testid="inserted-template-status"
                action={
                  <Button variant="secondary" size="sm" onClick={() => onRetry(set.answerSetId)}>
                    {retryLabel}
                  </Button>
                }
              >
                {STATUS_TEXT[set.status]}
              </Banner>
            ) : (
              <p className={`${V2_META} m-0`} role="status" data-testid="inserted-template-status">
                {STATUS_TEXT[set.status]}
              </p>
            ))}
        </div>
      </NoteBlock>
      <ModalYesNo
        open={confirming}
        title={`Remove ${set.title} from this note?`}
        message="Its answers will no longer show in this encounter."
        cancelLabel="Cancel"
        confirmLabel="Remove"
        destructive
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          onRemove(set.answerSetId);
        }}
      />
    </>
  );
}
